import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * A dynamic `import()` of a page chunk fails with one of these messages, depending on the browser/bundler:
 * Chrome/Vite "Failed to fetch dynamically imported module", Safari "Importing a module script failed",
 * Firefox "error loading dynamically imported module", webpack-style "Loading chunk N failed" / ChunkLoadError.
 */
const CHUNK_LOAD_ERROR =
  /failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module|loading (css )?chunk .* failed|ChunkLoadError/i;

/** True when `error` is a failure to LOAD a page chunk (network drop, or a chunk replaced by a deploy). */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? `${error.name} ${error.message}` : String(error ?? '');
  return CHUNK_LOAD_ERROR.test(message);
}

/** What the patient or clinician sees in place of a page that could not load. Rendered where the page would. */
export function RouteLoadError({ chunk, onReload }: { chunk: boolean; onReload?: () => void }) {
  const reload = onReload ?? (() => window.location.reload());
  return (
    <div
      className="flex min-h-[40vh] w-full flex-col items-center justify-center gap-3 p-6 text-center"
      role="alert"
      data-testid="route-load-error"
    >
      <p className="text-sm font-medium text-foreground">
        {chunk ? 'This page couldn’t be loaded.' : 'Something went wrong showing this page.'}
      </p>
      <p className="max-w-sm text-sm text-muted-foreground">
        {chunk
          ? 'Your connection may have dropped, or the app was updated while this tab was open. Reloading fixes it.'
          : 'Reloading usually fixes it. If it keeps happening, please call the clinic.'}
      </p>
      <button
        type="button"
        onClick={reload}
        className="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        data-testid="route-load-error-reload"
      >
        Reload
      </button>
    </div>
  );
}

interface BoundaryProps {
  children: ReactNode;
  /** Test seam; defaults to a full page reload (which also picks up a freshly deployed `index.html`). */
  onReload?: () => void;
}

interface BoundaryState {
  error?: unknown;
}

/**
 * Catches a page chunk that failed to load — and, as a side benefit, any render error in the page — so it
 * becomes a message with a Reload button instead of React unmounting the whole tree to a blank screen.
 *
 * Why this exists (#257): with every page in one file there was no way for navigation to fail to load code.
 * Once pages are lazy chunks, a flaky connection or a deploy that replaces the hashed chunks under an open tab
 * makes `import()` reject, and `React.lazy` has no handling of its own.
 */
export class RouteErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = {};

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { error };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('Route failed to render', error, info.componentStack);
  }

  render(): ReactNode {
    if ('error' in this.state && this.state.error !== undefined) {
      return <RouteLoadError chunk={isChunkLoadError(this.state.error)} onReload={this.props.onReload} />;
    }
    return this.props.children;
  }
}
